import * as mqtt from "precompiled-mqtt";
import { IConnectionSetting, IClusterReorderingValues } from "./ClusterReorderingInterfaces";
import { CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL, MQTTEventsEnum } from "constants/constants";
import { uniqueClientId } from "mqtt/uniqueClientId";

const { connect } = mqtt;

const clusterReorderingTopic = process.env.REACT_APP_CLUSTER_REORDERING_TOPIC || "cluster-reordering";
const clusterReorderingResponseTopicPrefix =
    process.env.REACT_APP_CLUSTER_REORDERING_RESPONSE_TOPIC_PREFIX || "cluster-reordering-response-";

export const ClusterReorderingMQTTEntity = {
    publishClusterReordering: (
        values: IClusterReorderingValues,
        options?: IConnectionSetting,
        clientId?: string,
        timeoutMs: number = CHECK_RESPONSE_TIMEOUT_LOCALSERVER_INTERVAL,
    ): Promise<{ success?: true; clusterId?: number; serverTime?: number; timestamp?: number; error?: string }> => {
        return new Promise((resolve, reject) => {
            if (!options) {
                reject("No broker settings provided");
                return;
            }

            const mosquittoUrl = options.host;
            const isWSS = mosquittoUrl.startsWith("wss");
            const responseTopic = `${clusterReorderingResponseTopicPrefix}${values.clusterId}`;
            const client = connect(mosquittoUrl, {
                username: options.user,
                password: options.password,
                protocol: isWSS ? "wss" : "ws",
                clean: true,
                clientId: uniqueClientId(clientId || `cluster_reorder_${values.clusterId}`),
                reconnectPeriod: 0,
            });

            let connectionHandled = false;

            const timeoutId = setTimeout(() => {
                client.end();
                reject(new Error("Request timed out"));
            }, timeoutMs);

            client.on(MQTTEventsEnum.ERROR, (err) => {
                connectionHandled = true;
                clearTimeout(timeoutId);
                client.end();
                reject(new Error(`MQTT client error: ${err.message}`));
            });

            client.on(MQTTEventsEnum.CONNECT, () => {
                if (connectionHandled) {
                    return;
                }
                connectionHandled = true;
                client.subscribe(responseTopic, (subErr) => {
                    if (subErr) {
                        clearTimeout(timeoutId);
                        client.end();
                        reject(new Error(`Failed to subscribe to ${responseTopic}`));
                        return;
                    }

                    try {
                        client.publish(clusterReorderingTopic, JSON.stringify(values), { qos: 1 });
                    } catch (pubErr) {
                        clearTimeout(timeoutId);
                        client.end();
                        reject(
                            new Error(
                                `Failed to publish cluster reordering: ${
                                    pubErr instanceof Error ? pubErr.message : String(pubErr)
                                }`,
                            ),
                        );
                    }
                });
            });

            client.on(MQTTEventsEnum.MESSAGE, (topic, message) => {
                if (topic !== responseTopic) {
                    return;
                }

                try {
                    const response = JSON.parse(message.toString()) as {
                        success?: true;
                        clusterId?: number;
                        serverTime?: number;
                        timestamp?: number;
                        error?: string;
                    };

                    if (response.error) {
                        clearTimeout(timeoutId);
                        client.end();
                        reject(new Error(response.error));
                        return;
                    }

                    if (
                        response.success &&
                        response.clusterId === values.clusterId &&
                        (response.timestamp === values.timestamp ||
                            (typeof response.timestamp === "number" && response.timestamp > values.timestamp))
                    ) {
                        clearTimeout(timeoutId);
                        client.end();
                        resolve(response);
                    }
                } catch (e) {
                    clearTimeout(timeoutId);
                    client.end();
                    reject(new Error("Failed to parse response"));
                }
            });

            client.on(MQTTEventsEnum.CLOSE, () => {
                // If connection closed before handling, surface error
                if (!connectionHandled) {
                    clearTimeout(timeoutId);
                    reject(new Error("MQTT connection failed"));
                }
            });
        });
    },
};
